import React from 'react';

export interface ScoreRingProps {
  score: number;
  size?: number;
}

type ToneKey = 'success' | 'warning' | 'amber' | 'danger';

const TONE_STROKE: Record<ToneKey, string> = {
  success: 'hsl(var(--status-success))',
  warning: 'hsl(var(--status-warning))',
  amber: 'var(--color-amber)',
  danger: 'hsl(var(--destructive))',
};

function pickTone(score: number): ToneKey {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  if (score >= 40) return 'amber';
  return 'danger';
}

export const ScoreRing: React.FC<ScoreRingProps> = ({ score, size = 32 }) => {
  const clamped = Math.max(0, Math.min(100, score));
  const tone = pickTone(clamped);
  const stroke = TONE_STROKE[tone];
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  const fontSize = size > 30 ? 11 : 9;

  return (
    <div
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
      aria-label={`Score ${clamped}`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(0,0,0,0.07)"
          strokeWidth={2.5}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeDasharray={`${(clamped / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span
        className="mono"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          fontSize,
          fontWeight: 700,
          color: 'var(--color-ink)',
          letterSpacing: '-0.02em',
        }}
      >
        {clamped}
      </span>
    </div>
  );
};

export default ScoreRing;
